import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Typography, NumberInput, Error, Button } from '@figure/frontlib-gui';
import { useAch, usePersonalLoan, usePersonalLoanRouteFsm } from 'shared/hooks';
import { isValidRoutingNumber } from 'shared/utils';
import { Stack, FormErrorHandler } from 'shared/components';
import { PageHeader } from 'shared/components/Typography';
import BaseCheckContainer from 'shared/components/Check/CheckContainer';
import CheckImage from 'shared/assets/images/check.svg';

const CheckContainer = styled(BaseCheckContainer)`
  margin-top: 16px;
`;

const ButtonContainer = styled.div`
  text-align: center;
`;

const ConfirmAccount = () => {
  const history = useHistory();
  const { items, getPlaidAssets } = useAch();
  const { personalLoan, saveDisburseAch, error: serverError } = usePersonalLoan();
  const runRouter = usePersonalLoanRouteFsm();
  const { t } = useTranslation();

  useEffect(() => {
    getPlaidAssets();
  }, [getPlaidAssets]);

  const item = items.find((i) =>
    i.accounts.some((a) => a.id === personalLoan.disburseAccount.achAccountId)
  ) || {
    institution: '',
    accounts: [],
  };
  const accountTypeDescription = (
    personalLoan.disburseAccount?.accountTypeDescription || ''
  ).toLowerCase();
  const accountNumberMask =
    item.accounts.find((a) => a.id === personalLoan.disburseAccount.achAccountId)
      ?.accountNumberMask || '';
  const accountNumberLastFour = accountNumberMask.slice(-4);

  const handleSubmit = async (data) => {
    await saveDisburseAch({
      plUuid: personalLoan.uuid,
      routingNumber: data.routingNumber,
      accountNumber: data.accountNumber,
      achAccountId: personalLoan.disburseAccount.achAccountId,
    });

    if (personalLoan.payType === 'AUTOPAY') {
      history.push(`/applications/${personalLoan.uuid}/bank/autopay`);
    } else {
      runRouter();
    }
  };

  const handleBack = () => history.push(`/applications/${personalLoan.uuid}/bank/disbursement`);

  return (
    <>
      <PageHeader>
        Please confirm the full routing and account number for the selected checking account.
      </PageHeader>
      <Typography.BodyBold>
        {`${item.institution} ${accountTypeDescription} account`}: XXXX-{accountNumberMask}
      </Typography.BodyBold>

      <Formik
        initialValues={{
          routingNumber: personalLoan.disburseAccount?.routingNumber ?? '',
          accountNumber: '',
        }}
        validationSchema={yup.lazy(() =>
          yup.object().shape({
            routingNumber: yup
              .string()
              .required('Required')
              .test(
                'is-routing-number',
                'Invalid',
                (value) => value && isValidRoutingNumber(value)
              ),
            accountNumber: yup
              .string()
              .required('Required')
              .min(4, 'At least 4 digits')
              .max(17, 'Up to 17 digits')
              .test(
                'is-account-number',
                'Invalid',
                (value) => !value || /^[0-9]{4,17}$/.test(value)
              )
              .test(
                'is-same-last-four',
                'Please enter the account number ending in the last 4 digits shown above.',
                (value) => value?.slice(-4) === accountNumberLastFour
              ),
          })
        )}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit }) => (
          <>
            <FormErrorHandler errors={errors} touched={touched} serverError={serverError} />
            <Form onSubmit={handleSubmit}>
              <Stack>
                <Stack space="1.6rem">
                  <CheckContainer>
                    <img
                      src={CheckImage}
                      alt="The bottom left side of a check has two groups of numbers. The first group is the routing number. The second group is the account number."
                    />
                  </CheckContainer>

                  <NumberInput
                    id="routing-number"
                    data-test-id="routing-number"
                    label="Routing number"
                    name="routing-number"
                    value={values.routingNumber}
                    isNumericString
                    format="#########"
                    errorText={touched.routingNumber && errors.routingNumber}
                    onChange={(v) => setFieldValue('routingNumber', v)}
                    required
                  />

                  <NumberInput
                    id="account-number"
                    data-test-id="account-number"
                    label="Account number"
                    name="account-number"
                    value={values.accountNumber}
                    isNumericString
                    maxLength="20"
                    errorText={touched.accountNumber && errors.accountNumber}
                    onChange={(v) => setFieldValue('accountNumber', v)}
                    required
                  />
                </Stack>

                <Typography.SmallBodyGrey>
                  By pressing “Save and continue,” you authorize {t('companySingleName')} to
                  disburse your funds to the account provided.
                </Typography.SmallBodyGrey>

                <Error display="block">{serverError}</Error>

                <ButtonContainer>
                  <Button large onClick={handleBack} type="button">
                    Back
                  </Button>

                  <Button onClick={handleSubmit} primary large>
                    Save and continue
                  </Button>
                </ButtonContainer>
              </Stack>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};

export default ConfirmAccount;
